import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import Dropzone from "@/components/Dropzone";
import TransparencyPreview from "@/components/TransparencyPreview";
import TopNav from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";
import { Download, Sparkles, Zap, Eraser, PaintBucket, Undo, RotateCcw, MousePointer, CircleDot, Shield, SlidersHorizontal, Sun, Contrast } from "lucide-react";

function makeObjectUrl(blob) {
  return URL.createObjectURL(blob);
}

export default function BackgroundRemoverPage() {
  const [tab, setTab] = useState("single");
  const [singleFiles, setSingleFiles] = useState([]);
  const [batchFiles, setBatchFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState("");
  const [outputBlob, setOutputBlob] = useState(null);
  const [outputUrl, setOutputUrl] = useState(null);
  const [outputName, setOutputName] = useState(null);

  // Brush editing state (post-processing)
  const [editMode, setEditMode] = useState(null); // null, "erase", "include"
  const [brushSize, setBrushSize] = useState(20);
  const [isDrawing, setIsDrawing] = useState(false);
  const [editHistory, setEditHistory] = useState([]);
  const canvasRef = useRef(null);
  const maskCanvasRef = useRef(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  
  // Pre-processing "keep mask" state - user marks areas to keep BEFORE processing
  const [keepMaskMode, setKeepMaskMode] = useState(false);
  const [keepBrushSize, setKeepBrushSize] = useState(30);
  const [keepMaskData, setKeepMaskData] = useState(null);
  const keepCanvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [isDrawingKeep, setIsDrawingKeep] = useState(false);
  const canvasInitializedRef = useRef(false);
  
  // Edge adjustment sliders
  const [showAdjustments, setShowAdjustments] = useState(false);
  const [edgeCut, setEdgeCut] = useState(0);
  const [edgeSmooth, setEdgeSmooth] = useState(0);

  const currentFiles = tab === "single" ? singleFiles : batchFiles;
  const canRun = useMemo(() => currentFiles.length > 0 && !busy && !keepMaskMode, [busy, currentFiles.length, keepMaskMode]);

  const resetOutput = () => {
    if (outputUrl) URL.revokeObjectURL(outputUrl);
    setOutputBlob(null);
    setOutputUrl(null);
    setOutputName(null);
    setEditMode(null);
    setEditHistory([]);
    setOriginalImage(null);
    setProcessedImage(null);
    setKeepMaskMode(false);
    setKeepMaskData(null);
  };
  
  // Initialize canvas when output is ready
  useEffect(() => {
    if (outputUrl && canvasRef.current && tab === "single") {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        setProcessedImage(img);
        
        // Also load original image for include mode
        if (singleFiles[0]) {
          const origImg = new Image();
          origImg.onload = () => setOriginalImage(origImg);
          origImg.src = URL.createObjectURL(singleFiles[0]);
        }
      };
      img.src = outputUrl;
    }
  }, [outputUrl, singleFiles, tab]);

  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const saveToHistory = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const imageData = canvas.getContext("2d").getImageData(0, 0, canvas.width, canvas.height);
    setEditHistory(prev => [...prev.slice(-10), imageData]); // Keep last 10 states
  };

  const handleMouseDown = (e) => {
    if (!editMode || !canvasRef.current) return;
    saveToHistory();
    setIsDrawing(true);
    draw(e);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !editMode) return;
    draw(e);
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
    updateOutputFromCanvas();
  };

  const draw = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCanvasCoords(e);

    ctx.beginPath();
    ctx.arc(x, y, brushSize / 2, 0, Math.PI * 2);

    if (editMode === "erase") {
      // Erase to transparency
      ctx.globalCompositeOperation = "destination-out";
      ctx.fill();
      ctx.globalCompositeOperation = "source-over";
    } else if (editMode === "include" && originalImage) {
      // Restore from original image
      ctx.save();
      ctx.globalCompositeOperation = "source-over";
      ctx.clip();
      ctx.drawImage(originalImage, 0, 0, canvas.width, canvas.height);
      ctx.restore();
    }
  };
  
  // Keep mask drawing functions (for marking areas to keep BEFORE processing)
  const getKeepCanvasCoords = (e) => {
    const previewCanvas = previewCanvasRef.current;
    if (!previewCanvas) return { x: 0, y: 0 };
    
    const rect = previewCanvas.getBoundingClientRect();
    
    // Calculate position relative to the canvas
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    return { x, y };
  };
  
  const handleKeepMouseDown = (e) => {
    if (!keepMaskMode) return;
    setIsDrawingKeep(true);
    drawKeepMask(e);
  };
  
  const handleKeepMouseMove = (e) => {
    if (!isDrawingKeep || !keepMaskMode) return;
    drawKeepMask(e);
  };
  
  const handleKeepMouseUp = () => {
    setIsDrawingKeep(false);
  };
  
  const drawKeepMask = (e) => {
    const previewCanvas = previewCanvasRef.current;
    const keepCanvas = keepCanvasRef.current;
    if (!previewCanvas || !keepCanvas || !singleFiles[0]) {
      console.log('drawKeepMask: missing refs', !!previewCanvas, !!keepCanvas, !!singleFiles[0]);
      return;
    }
    
    // Ensure canvas has dimensions
    if (previewCanvas.width === 0 || previewCanvas.height === 0) {
      const img = document.getElementById('keep-mask-preview-img');
      if (img) {
        previewCanvas.width = img.clientWidth;
        previewCanvas.height = img.clientHeight;
        keepCanvas.width = img.naturalWidth || img.clientWidth;
        keepCanvas.height = img.naturalHeight || img.clientHeight;
        console.log('Canvas dimensions set:', previewCanvas.width, 'x', previewCanvas.height);
      }
    }
    
    const previewCtx = previewCanvas.getContext("2d");
    const keepCtx = keepCanvas.getContext("2d");
    const { x, y } = getKeepCanvasCoords(e);
    
    console.log('Drawing at:', x, y, 'Canvas size:', previewCanvas.width, 'x', previewCanvas.height);
    
    // Ignore clicks outside the canvas bounds
    if (x < 0 || y < 0 || x > previewCanvas.width || y > previewCanvas.height) {
      console.log('Out of bounds');
      return;
    }
    
    // Calculate scale between preview canvas and keep canvas (full resolution)
    const scaleX = keepCanvas.width / previewCanvas.width;
    const scaleY = keepCanvas.height / previewCanvas.height;
    
    // Draw on preview canvas (visible) - 70% transparent green
    previewCtx.beginPath();
    previewCtx.arc(x, y, keepBrushSize / 2, 0, Math.PI * 2);
    previewCtx.fillStyle = "rgba(0, 255, 0, 0.3)";
    previewCtx.fill();
    
    // Draw on keep mask canvas (full resolution, hidden) - solid green
    keepCtx.beginPath();
    keepCtx.arc(x * scaleX, y * scaleY, (keepBrushSize / 2) * scaleX, 0, Math.PI * 2);
    keepCtx.fillStyle = "rgb(0, 255, 0)";
    keepCtx.fill();
  };
  
  const clearKeepMask = () => {
    const keepCanvas = keepCanvasRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!keepCanvas || !previewCanvas) return;
    
    // Clear both canvases
    const keepCtx = keepCanvas.getContext("2d");
    keepCtx.clearRect(0, 0, keepCanvas.width, keepCanvas.height);
    
    const previewCtx = previewCanvas.getContext("2d");
    previewCtx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
  };
  
  const confirmKeepMask = () => {
    const keepCanvas = keepCanvasRef.current;
    if (keepCanvas) {
      // Convert canvas to data URL for sending to backend
      setKeepMaskData(keepCanvas.toDataURL("image/png"));
    }
    setKeepMaskMode(false);
  };
  
  // Initialize canvases when modal opens
  useEffect(() => {
    if (keepMaskMode && singleFiles[0] && keepCanvasRef.current && previewCanvasRef.current) {
      // Reset the initialization flag when modal opens
      canvasInitializedRef.current = false;
      
      // Small delay to ensure image is rendered and we can get its bounds
      const initCanvases = () => {
        if (canvasInitializedRef.current) return; // Already initialized
        
        const keepCanvas = keepCanvasRef.current;
        const previewCanvas = previewCanvasRef.current;
        const imgElement = document.getElementById('keep-mask-preview-img');
        
        if (!keepCanvas || !previewCanvas || !imgElement || imgElement.clientWidth === 0) {
          // Retry if image not ready
          setTimeout(initCanvases, 100);
          return;
        }
        
        // Set preview canvas to match the displayed image dimensions
        previewCanvas.width = imgElement.clientWidth;
        previewCanvas.height = imgElement.clientHeight;
        previewCanvas.style.width = imgElement.clientWidth + 'px';
        previewCanvas.style.height = imgElement.clientHeight + 'px';
        
        // Set keep canvas to original image size (full resolution for backend)
        keepCanvas.width = imgElement.naturalWidth || imgElement.clientWidth;
        keepCanvas.height = imgElement.naturalHeight || imgElement.clientHeight;
        
        // Clear both canvases
        keepCanvas.getContext("2d").clearRect(0, 0, keepCanvas.width, keepCanvas.height);
        previewCanvas.getContext("2d").clearRect(0, 0, previewCanvas.width, previewCanvas.height);
        
        canvasInitializedRef.current = true;
        console.log(`Canvas initialized: preview=${previewCanvas.width}x${previewCanvas.height}, keep=${keepCanvas.width}x${keepCanvas.height}`);
      };
      
      setTimeout(initCanvases, 200);
    }
  }, [keepMaskMode, singleFiles]);

  const updateOutputFromCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        if (outputUrl && outputUrl !== url) URL.revokeObjectURL(outputUrl);
        setOutputBlob(blob);
        setOutputUrl(url);
      }
    }, "image/png");
  };

  const undoLastEdit = () => {
    if (editHistory.length === 0) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const lastState = editHistory[editHistory.length - 1];
    ctx.putImageData(lastState, 0, 0);
    setEditHistory(prev => prev.slice(0, -1));
    updateOutputFromCanvas();
  };

  const resetEdits = () => {
    if (!processedImage || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(processedImage, 0, 0);
    setEditHistory([]);
    updateOutputFromCanvas();
  };

  const runSingle = async () => {
    if (!singleFiles.length) return;
    resetOutput();
    setBusy(true);
    setProgress(10);
    setProgressText("Uploading...");

    try {
      const form = new FormData();
      form.append("file", singleFiles[0]);
      
      // Add keep mask if user marked areas to keep
      if (keepMaskData) {
        form.append("keep_mask", keepMaskData);
      }
      
      // Add edge adjustment parameters
      if (edgeCut > 0) {
        form.append("edge_cut", String(edgeCut));
      }
      if (edgeSmooth > 0) {
        form.append("edge_smooth", String(edgeSmooth));
      }

      setProgressText("Removing background...");
      const res = await api.post("/remove-bg", form, {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob",
        timeout: 300000,
        onUploadProgress: (e) => {
          if (!e.total) return;
          const pct = Math.round((e.loaded / e.total) * 25);
          setProgress(Math.min(25, pct));
          if (pct >= 25) setProgressText("Processing image...");
        },
      });

      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^\";]+)"?/i);
      const filename = m?.[1] || "output-nobg.png";

      setProgressText("Finalizing...");
      setOutputBlob(res.data);
      setOutputName(filename);
      setOutputUrl(makeObjectUrl(res.data));
      setProgress(100);
      setKeepMaskData(null); // Clear keep mask after processing

      toast({
        title: "Done",
        description: "Background removed with sharp edges. Use brush tools to refine.",
      });
    } catch (e) {
      console.error(e);
      toast({
        title: "Error",
        description: e?.response?.data?.detail || e?.message || "Could not remove background. Please try again.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
      setProgressText("");
      setTimeout(() => setProgress(0), 900);
    }
  };

  const runBatch = async () => {
    if (!batchFiles.length) return;
    resetOutput();
    setBusy(true);
    setProgress(10);

    try {
      const form = new FormData();
      batchFiles.forEach((f) => form.append("files", f));

      const res = await api.post("/remove-bg-batch", form, {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob",
        onUploadProgress: (e) => {
          if (!e.total) return;
          const pct = Math.round((e.loaded / e.total) * 30);
          setProgress(Math.min(30, pct));
        },
      });

      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^\";]+)"?/i);
      const filename = m?.[1] || "genx-bg-removed.zip";

      setOutputBlob(res.data);
      setOutputName(filename);
      setOutputUrl(makeObjectUrl(res.data));
      setProgress(100);

      toast({ title: "Batch complete", description: "ZIP is ready to download." });
    } catch (e) {
      console.error(e);
      toast({
        title: "Error",
        description: e?.response?.data?.detail || "Could not process batch.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
      setTimeout(() => setProgress(0), 900);
    }
  };

  const onRun = () => (tab === "single" ? runSingle() : runBatch());

  const download = () => {
    if (!outputBlob || !outputUrl) return;
    const a = document.createElement("a");
    a.href = outputUrl;
    a.download = outputName || (tab === "single" ? "output-nobg.png" : "genx-bg-removed.zip");
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Download with white background (re-process with output=white)
  const downloadOnWhite = async () => {
    if (!singleFiles[0]) return;
    try {
      const formData = new FormData();
      formData.append("file", singleFiles[0]);
      formData.append("output", "white");
      
      const res = await api.post("/remove-bg", formData, {
        responseType: "blob",
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      const blob = new Blob([res.data], { type: "image/png" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "output-white-bg.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      
      toast({ title: "Downloaded", description: "Image with white background saved" });
    } catch (err) {
      toast({ title: "Download failed", description: err.message, variant: "destructive" });
    }
  };

  const heroStats = (
    <div className="flex flex-wrap items-center gap-2" data-testid="hero-badges">
      <Badge variant="secondary" className="rounded-full bg-white/5 text-zinc-200 ring-1 ring-white/10">
        <Zap className="mr-1 h-3.5 w-3.5" /> Offline
      </Badge>
      <Badge variant="secondary" className="rounded-full bg-white/5 text-zinc-200 ring-1 ring-white/10">
        Transparent PNG
      </Badge>
      <Badge variant="secondary" className="rounded-full bg-violet-500/20 text-violet-200 ring-1 ring-violet-400/30">
        Sharp Edges
      </Badge>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#07070a] text-zinc-100" data-testid="bg-remover-page">
      <TopNav />
      <div
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background:
            "radial-gradient(900px 400px at 20% 0%, rgba(139,92,246,0.25), transparent 60%), radial-gradient(900px 400px at 80% 10%, rgba(34,211,238,0.10), transparent 55%)",
        }}
      />

      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="space-y-5">
              {heroStats}
              <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
                GenX Background Remover
              </h1>
              <p className="text-base text-zinc-300 md:text-lg">
                Drag, drop, remove — export transparent PNGs with sharp edges. Use brush tools to refine.
              </p>

              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 grid h-9 w-9 place-items-center rounded-xl bg-violet-500/15 ring-1 ring-violet-300/20">
                    <Sparkles className="h-5 w-5 text-violet-200" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-zinc-100">Privacy-first + Sharp Edges</div>
                    <div className="mt-1 text-sm text-zinc-400">
                      Images processed locally with edge refinement. No uploads, no accounts.
                    </div>
                  </div>
                </div>
              </div>

              {progress > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-zinc-400">
                    <span>{progressText || "Processing"}</span>
                    <span>{progress}%</span>
                  </div>
                  <Progress value={progress} />
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3">
                {/* Mark Areas to Keep button - only for single mode before processing */}
                {tab === "single" && singleFiles.length > 0 && !outputUrl && (
                  <Button
                    onClick={() => setKeepMaskMode(true)}
                    disabled={busy}
                    variant="secondary"
                    className="rounded-full bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30 hover:bg-emerald-500/30"
                  >
                    <Shield className="mr-2 h-4 w-4" />
                    Mark areas to keep
                  </Button>
                )}
                <Button
                  onClick={onRun}
                  disabled={!canRun}
                  className="rounded-full bg-violet-500 text-white hover:bg-violet-400 disabled:opacity-50"
                >
                  {tab === "single" ? "Remove background" : "Process batch"}
                </Button>
                <Button
                  onClick={download}
                  disabled={!outputUrl}
                  variant="secondary"
                  className="rounded-full bg-white/5 text-zinc-100 ring-1 ring-white/10 hover:bg-white/10 disabled:opacity-50"
                  data-testid="download-transparent-btn"
                >
                  <Download className="mr-2 h-4 w-4" />
                  {tab === "single" ? "Transparent PNG" : "Download ZIP"}
                </Button>
                {tab === "single" && outputUrl && (
                  <Button
                    onClick={downloadOnWhite}
                    variant="secondary"
                    className="rounded-full bg-white/10 text-zinc-100 ring-1 ring-white/20 hover:bg-white/15"
                    data-testid="download-white-btn"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    On White BG
                  </Button>
                )}
              </div>
              
              {/* Keep Mask Indicator */}
              {keepMaskData && !outputUrl && (
                <div className="flex items-center gap-2 text-sm text-emerald-400">
                  <Shield className="h-4 w-4" />
                  <span>Areas marked for preservation</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setKeepMaskData(null)}
                    className="text-xs text-zinc-400 hover:text-zinc-200"
                  >
                    Clear
                  </Button>
                </div>
              )}
              
              {/* Edge Adjustment Sliders - Show before processing */}
              {tab === "single" && singleFiles.length > 0 && !outputUrl && (
                <div className="rounded-2xl border border-white/10 bg-zinc-900/50 p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-zinc-100 flex items-center gap-2">
                      <SlidersHorizontal className="h-4 w-4 text-violet-400" />
                      Edge Adjustments
                    </div>
                    <Switch
                      checked={showAdjustments}
                      onCheckedChange={setShowAdjustments}
                      data-testid="show-adjustments-switch"
                    />
                  </div>
                  
                  {showAdjustments && (
                    <div className="space-y-4 pt-2">
                      {/* Edge Cut Slider */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-zinc-300">Edge Cut</span>
                          <span className="text-xs text-zinc-500">{edgeCut}px</span>
                        </div>
                        <Slider
                          value={[edgeCut]}
                          onValueChange={([v]) => setEdgeCut(v)}
                          min={0}
                          max={50}
                          step={1}
                          className="w-full"
                          data-testid="edge-cut-slider"
                        />
                        <p className="text-xs text-zinc-500">
                          Cut into edges to remove halos (0 = soft feather, higher = crisp edge)
                        </p>
                      </div>
                      
                      {/* Smooth Edge Slider */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-zinc-300">Smooth Edge</span>
                          <span className="text-xs text-zinc-500">{edgeSmooth}px</span>
                        </div>
                        <Slider
                          value={[edgeSmooth]}
                          onValueChange={([v]) => setEdgeSmooth(v)}
                          min={0}
                          max={20}
                          step={1}
                          className="w-full"
                          data-testid="edge-smooth-slider"
                        />
                        <p className="text-xs text-zinc-500">
                          Soften final edge for natural blend
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Brush Tools - Only show for single mode with output */}
              {tab === "single" && outputUrl && (
                <div className="rounded-2xl border border-white/10 bg-zinc-900/50 p-4 space-y-4">
                  <div className="text-sm font-medium text-zinc-100">Refine Edges</div>
                  
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant={editMode === null ? "default" : "secondary"}
                      onClick={() => setEditMode(null)}
                      className={`rounded-full ${editMode === null ? "bg-violet-500" : "bg-white/5 ring-1 ring-white/10"}`}
                    >
                      <MousePointer className="mr-1 h-4 w-4" /> Select
                    </Button>
                    <Button
                      size="sm"
                      variant={editMode === "erase" ? "default" : "secondary"}
                      onClick={() => setEditMode("erase")}
                      className={`rounded-full ${editMode === "erase" ? "bg-red-500" : "bg-white/5 ring-1 ring-white/10"}`}
                    >
                      <Eraser className="mr-1 h-4 w-4" /> Erase
                    </Button>
                    <Button
                      size="sm"
                      variant={editMode === "include" ? "default" : "secondary"}
                      onClick={() => setEditMode("include")}
                      className={`rounded-full ${editMode === "include" ? "bg-emerald-500" : "bg-white/5 ring-1 ring-white/10"}`}
                    >
                      <PaintBucket className="mr-1 h-4 w-4" /> Include
                    </Button>
                  </div>

                  {editMode && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs text-zinc-400">
                        <span>Brush Size</span>
                        <span>{brushSize}px</span>
                      </div>
                      <Slider
                        value={[brushSize]}
                        onValueChange={([v]) => setBrushSize(v)}
                        min={5}
                        max={100}
                        step={5}
                        className="w-full"
                      />
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={undoLastEdit}
                      disabled={editHistory.length === 0}
                      className="rounded-full bg-white/5 ring-1 ring-white/10"
                    >
                      <Undo className="mr-1 h-4 w-4" /> Undo
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={resetEdits}
                      className="rounded-full bg-white/5 ring-1 ring-white/10"
                    >
                      <RotateCcw className="mr-1 h-4 w-4" /> Reset
                    </Button>
                  </div>

                  <div className="text-xs text-zinc-500">
                    <strong>Erase:</strong> Remove areas from the result<br/>
                    <strong>Include:</strong> Restore areas from original
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-7">
            <Card className="rounded-3xl border-white/10 bg-zinc-950/50 shadow-[0_18px_70px_rgba(0,0,0,0.45)]">
              <CardHeader className="pb-2">
                <CardTitle className="text-base md:text-lg">Upload</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <Tabs value={tab} onValueChange={setTab}>
                  <TabsList className="grid w-full grid-cols-2 rounded-2xl bg-white/5">
                    <TabsTrigger
                      value="single"
                      className="rounded-2xl"
                      onClick={() => { setTab("single"); resetOutput(); }}
                    >
                      Single
                    </TabsTrigger>
                    <TabsTrigger
                      value="batch"
                      className="rounded-2xl"
                      onClick={() => { setTab("batch"); resetOutput(); }}
                    >
                      Batch
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="single" className="mt-6">
                    <Dropzone mode="single" files={singleFiles} setFiles={setSingleFiles} maxFiles={1} />
                  </TabsContent>

                  <TabsContent value="batch" className="mt-6">
                    <Dropzone mode="batch" files={batchFiles} setFiles={setBatchFiles} maxFiles={120} />
                    <div className="mt-3 text-xs text-zinc-500">
                      Tip: for very large images, batch might take longer. Start with 10–20 files.
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-zinc-200">Input</div>
                    <div className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/40">
                      <div className="h-64">
                        {currentFiles[0] ? (
                          <img
                            src={URL.createObjectURL(currentFiles[0])}
                            alt="input"
                            className="h-full w-full object-contain"
                          />
                        ) : (
                          <div className="grid h-full place-items-center text-sm text-zinc-400">
                            Choose a file to preview
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="text-sm font-medium text-zinc-200">
                      Output {editMode && <span className="text-xs text-violet-400">(Click to edit)</span>}
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/40 relative">
                      <div
                        className="h-64 flex items-center justify-center"
                        style={{
                          backgroundImage:
                            "linear-gradient(45deg, #1a1a1a 25%, transparent 25%), linear-gradient(-45deg, #1a1a1a 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #1a1a1a 75%), linear-gradient(-45deg, transparent 75%, #1a1a1a 75%)",
                          backgroundSize: "20px 20px",
                          backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
                        }}
                      >
                        {outputUrl && tab === "single" ? (
                          <canvas
                            ref={canvasRef}
                            style={{ 
                              cursor: editMode === "erase" ? "crosshair" : editMode === "include" ? "cell" : "default",
                              maxWidth: "100%",
                              maxHeight: "256px",
                              objectFit: "contain"
                            }}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                          />
                        ) : outputUrl ? (
                          <TransparencyPreview src={outputUrl} alt="output" />
                        ) : (
                          <div className="grid h-full w-full place-items-center text-sm text-zinc-400">
                            Result will appear here
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-zinc-300">
              <div className="font-medium text-zinc-100">Sharp Edge Processing</div>
              <div className="mt-1 text-sm text-zinc-400">
                Uses alpha matting and edge refinement for clean, crisp cutouts. Use Erase/Include tools for manual touch-ups.
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Keep Mask Modal */}
      <Dialog open={keepMaskMode} onOpenChange={setKeepMaskMode}>
        <DialogContent className="max-w-2xl bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Shield className="h-5 w-5 text-emerald-400" />
              Mark Areas to Keep
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">
              Use the brush to mark areas you want to <strong className="text-emerald-400">keep</strong> (clothes, shoes, accessories). 
              These areas will be protected during background removal.
            </p>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-300">Brush Size: {keepBrushSize}px</span>
              </div>
              <Slider
                value={[keepBrushSize]}
                onValueChange={([v]) => setKeepBrushSize(v)}
                min={10}
                max={100}
                step={5}
                className="w-full"
              />
            </div>
            
            <div 
              className="relative rounded-lg overflow-hidden border border-white/10 bg-zinc-950 flex items-center justify-center"
              style={{ minHeight: "300px" }}
            >
              {/* Show the original image */}
              {singleFiles[0] && (
                <div className="relative inline-block">
                  <img 
                    id="keep-mask-preview-img"
                    src={URL.createObjectURL(singleFiles[0])} 
                    alt="preview" 
                    className="max-h-[400px] max-w-full"
                    style={{ display: "block" }}
                    onLoad={(e) => {
                      // Only initialize canvas once per modal open
                      if (canvasInitializedRef.current) return;
                      
                      const img = e.target;
                      const previewCanvas = previewCanvasRef.current;
                      const keepCanvas = keepCanvasRef.current;
                      if (previewCanvas && keepCanvas && img.clientWidth > 0) {
                        // Set canvas HTML attributes (important for drawing context)
                        previewCanvas.width = img.clientWidth;
                        previewCanvas.height = img.clientHeight;
                        // Also set CSS size to match
                        previewCanvas.style.width = img.clientWidth + 'px';
                        previewCanvas.style.height = img.clientHeight + 'px';
                        
                        // Set keep canvas to natural (full) image size
                        keepCanvas.width = img.naturalWidth;
                        keepCanvas.height = img.naturalHeight;
                        
                        canvasInitializedRef.current = true;
                        console.log('Canvas initialized:', previewCanvas.width, 'x', previewCanvas.height);
                      }
                    }}
                  />
                  {/* Overlay canvas for drawing - positioned exactly over the image */}
                  <canvas
                    ref={previewCanvasRef}
                    width={400}
                    height={400}
                    className="absolute top-0 left-0 cursor-crosshair"
                    style={{ 
                      pointerEvents: "auto",
                    }}
                    onMouseDown={handleKeepMouseDown}
                    onMouseMove={handleKeepMouseMove}
                    onMouseUp={handleKeepMouseUp}
                    onMouseLeave={handleKeepMouseUp}
                    onTouchStart={(e) => { e.preventDefault(); handleKeepMouseDown(e.touches[0]); }}
                    onTouchMove={(e) => { e.preventDefault(); handleKeepMouseMove(e.touches[0]); }}
                    onTouchEnd={handleKeepMouseUp}
                  />
                </div>
              )}
              {/* Hidden full-resolution keep mask canvas */}
              <canvas ref={keepCanvasRef} className="hidden" />
            </div>
            
            <div className="text-xs text-zinc-500">
              Draw on the image to mark areas in <span className="text-emerald-400">green</span>. These areas will be preserved.
            </div>
            
            <div className="flex items-center justify-between gap-3">
              <Button
                variant="secondary"
                onClick={clearKeepMask}
                className="bg-white/5 text-zinc-300 ring-1 ring-white/10"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Clear
              </Button>
              
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setKeepMaskMode(false)}
                  className="bg-white/5 text-zinc-300 ring-1 ring-white/10"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmKeepMask}
                  className="bg-emerald-500 text-white hover:bg-emerald-400"
                >
                  <Shield className="mr-2 h-4 w-4" />
                  Apply Mask
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
